import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { Exam, MockScore, NoteInbox, StudyLog, WrongNote } from '@hoedokloop/domain';
import { usePlatform } from '../platform/platform-context';
import { PhotoViewer } from '../components/photo-viewer';
type Snapshot = {
  exams: Exam[];
  logs: StudyLog[];
  notes: WrongNote[];
  scores: MockScore[];
  inbox: NoteInbox[];
};
/** Stable read/export path for a compatible recovery Worker version; never downgrades or deletes data. */
export function RecoveryPage() {
  const { studyRepository } = usePlatform();
  const [data, setData] = useState<Snapshot>();
  const [error, setError] = useState<string>();
  const load = async () => {
    const [exams, logs, notes, scores, inbox] = await Promise.all([
      studyRepository.list('exams', { includeDeleted: true }),
      studyRepository.list('studyLogs', { includeDeleted: true }),
      studyRepository.list('wrongNotes', { includeDeleted: true }),
      studyRepository.list('mockScores', { includeDeleted: true }),
      studyRepository.list('noteInbox', { includeDeleted: true }),
    ]);
    if (!exams.ok || !logs.ok || !notes.ok || !scores.ok || !inbox.ok) {
      setError('기록을 열지 못했어요. 저장소를 지우지 말고 다시 시도해 주세요.');
      return;
    }
    setData({
      exams: exams.value,
      logs: logs.value,
      notes: notes.value,
      scores: scores.value,
      inbox: inbox.value,
    });
    setError(undefined);
  };
  useEffect(() => {
    void load();
  }, [studyRepository]);
  return (
    <div className="pn-page">
      <h1>내 기록 열기와 백업</h1>
      <p>
        기록을 지우거나 저장 형식을 되돌리지 않아요. 백업 파일을 먼저 받아 두고, 아래에서 원래
        기록을 확인해 주세요.
      </p>
      <Link className="pn-primary-link" to="/settings/data">
        전체 백업·복원 열기
      </Link>
      {error ? (
        <p role="alert">
          {error}
          <button onClick={() => void load()}>다시 불러오기</button>
        </p>
      ) : !data ? (
        <p role="status">기록을 여는 중이에요.</p>
      ) : (
        <>
          <section>
            <h2>시험 {data.exams.length}개</h2>
            {data.exams.map((exam) => (
              <p key={exam.id}>
                {exam.title} · {exam.examDate ?? '시험일 미정'} · {exam.result ?? '결과 미입력'}
              </p>
            ))}
          </section>
          <section>
            <h2>공부 기록 {data.logs.length}개</h2>
            {data.logs.map((log) => (
              <details key={log.id}>
                <summary>
                  {log.studyDate} · {log.round}회독 {log.unitStart}–{log.unitEnd}
                </summary>
                <p>{log.memo}</p>
                <p>{log.minutes === undefined ? '시간 미입력' : `${log.minutes}분`}</p>
              </details>
            ))}
          </section>
          <section>
            <h2>오답 {data.notes.length}개</h2>
            {data.notes.map((note) => (
              <details key={note.id}>
                <summary>{note.text || '사진 오답'}</summary>
                <p>{note.memo}</p>
                {note.photoAssetId ? (
                  <PhotoViewer assetId={note.photoAssetId} alt="보관한 오답 원본" />
                ) : null}
              </details>
            ))}
          </section>
          <section>
            <h2>점수 {data.scores.length}회</h2>
            {data.scores.map((score) => (
              <p key={score.id}>
                {score.studyDate} · {score.title} ·{' '}
                {score.scores.map((item) => item.score).join(' / ')}점
              </p>
            ))}
          </section>
          <section>
            <h2>사진함 {data.inbox.length}장</h2>
            {data.inbox.map((item) => (
              <PhotoViewer key={item.id} assetId={item.photoAssetId} alt="사진함 원본" />
            ))}
          </section>
        </>
      )}
    </div>
  );
}
